import { memo, useCallback, useMemo } from 'react';
import { Gallery, cn } from '@divlab/divanui';

import useMedias from '@Hooks/useMedias';
import FabricCard from '../FabricCard';
import styles from './Collection.module.css';

import type { FabricData } from '../FabricCard';
import type { FC, HTMLAttributes, MouseEvent } from 'react';
import type { ConfiguratorOption, ConfiguratorOptionItemFabric } from '@Pages/PageProduct/typings';

interface CollectionProps {
  collection: ConfiguratorOption;
  className?: string;
  tabName: string;
  onClickItem?: (e: MouseEvent, item: ConfiguratorOptionItemFabric) => void;
  onClickCollection?: (item: ConfiguratorOption) => void;
}

const Container: FC<HTMLAttributes<HTMLDivElement>> = ({ children, ...restProps }) => {
  const { isMobile } = useMedias();
  return isMobile ? (
    <Gallery className={styles.fabrics} spaceBetween={10} {...restProps}>
      {children}
    </Gallery>
  ) : (
    <div className={styles.fabrics} {...restProps}>
      {children}
    </div>
  );
};

const Collection: FC<CollectionProps> = ({
  collection,
  tabName,
  onClickItem,
  onClickCollection,
  ...restProps
}) => {
  const isOutlet = collection.id === 'outlet';
  const isBase = collection.id === 'baseCollection';
  const hasDescription = !!collection.description && collection.images?.length > 0;

  const needShowFabricPrice =
    isBase ||
    isOutlet ||
    !collection.items.every((fabric) => fabric.price.actual === collection.minPrice?.actual);

  const filteredCollection = useMemo(() => {
    return {
      ...collection,
      items: collection.items.filter((item: ConfiguratorOptionItemFabric) => !item.hidden),
    };
  }, [collection]);

  const handleCollectionClick = useCallback(() => {
    if (isOutlet || !hasDescription) return;
    onClickCollection?.(collection);
  }, [isOutlet, hasDescription, onClickCollection, collection]);

  const renderFabricCard = useCallback(
    (fabric: FabricData, index: number) => {
      return (
        <FabricCard
          data-testid={`color${index + 1}`}
          fabric={fabric}
          collection={collection}
          tabName={tabName}
          onClickItem={onClickItem}
          onClickCollection={handleCollectionClick}
          needShowPrice={needShowFabricPrice}
        />
      );
    },
    [collection, handleCollectionClick, needShowFabricPrice, onClickItem, tabName],
  );

  return (
    <div {...restProps}>
      <Container>
        {filteredCollection.items.map((fabric: ConfiguratorOptionItemFabric, index) => (
          <div
            key={`${fabric.id}_${collection.id}`}
            className={cn(styles.fabricItem, { [styles.hidden]: fabric.hidden })}
          >
            {renderFabricCard({ ...fabric, view: 'grouped' }, index)}
          </div>
        ))}
      </Container>
    </div>
  );
};

export default memo(Collection);
